export interface HexCoord {
  q: number;
  r: number;
}

export interface Point {
  x: number;
  y: number;
}

export enum HexOrientation {
  POINTY = 'pointy',
  FLAT = 'flat'
}

export class HexMath {
  static readonly SQRT3 = Math.sqrt(3);

  static offsetToAxial(col: number, row: number, orientation: HexOrientation): HexCoord {
    if (orientation === HexOrientation.POINTY) {
      const q = col - (row - (row & 1)) / 2;
      const r = row;
      return { q, r };
    } else {
      const q = col;
      const r = row - (col - (col & 1)) / 2;
      return { q, r };
    }
  }

  static axialToOffset(q: number, r: number, orientation: HexOrientation): { col: number; row: number } {
    if (orientation === HexOrientation.POINTY) {
      const col = q + (r - (r & 1)) / 2;
      const row = r;
      return { col, row };
    } else {
      const col = q;
      const row = r + (q - (q & 1)) / 2;
      return { col, row };
    }
  }

  static getHexNeighbors(q: number, r: number): HexCoord[] {
    const directions = [
      { q: 1, r: 0 },
      { q: 1, r: -1 },
      { q: 0, r: -1 },
      { q: -1, r: 0 },
      { q: -1, r: 1 },
      { q: 0, r: 1 }
    ];

    return directions.map(dir => ({
      q: q + dir.q,
      r: r + dir.r
    }));
  }

  static hexDistance(q1: number, r1: number, q2: number, r2: number): number {
    const s1 = -q1 - r1;
    const s2 = -q2 - r2;
    return (Math.abs(q1 - q2) + Math.abs(r1 - r2) + Math.abs(s1 - s2)) / 2;
  }

  static hexToPixel(q: number, r: number, size: number, orientation: HexOrientation): Point {
    if (orientation === HexOrientation.POINTY) {
      const x = size * (this.SQRT3 * q + this.SQRT3 / 2 * r);
      const y = size * (3 / 2 * r);
      return { x, y };
    } else {
      const x = size * (3 / 2 * q);
      const y = size * (this.SQRT3 / 2 * q + this.SQRT3 * r);
      return { x, y };
    }
  }

  static pixelToHex(x: number, y: number, size: number, orientation: HexOrientation): HexCoord {
    if (orientation === HexOrientation.POINTY) {
      const q = (this.SQRT3 / 3 * x - 1 / 3 * y) / size;
      const r = (2 / 3 * y) / size;
      return this.roundHex(q, r);
    } else {
      const q = (2 / 3 * x) / size;
      const r = (-1 / 3 * x + this.SQRT3 / 3 * y) / size;
      return this.roundHex(q, r);
    }
  }

  static roundHex(q: number, r: number): HexCoord {
    const s = -q - r;

    let rq = Math.round(q);
    let rr = Math.round(r);
    let rs = Math.round(s);

    const qDiff = Math.abs(rq - q);
    const rDiff = Math.abs(rr - r);
    const sDiff = Math.abs(rs - s);

    if (qDiff > rDiff && qDiff > sDiff) {
      rq = -rr - rs;
    } else if (rDiff > sDiff) {
      rr = -rq - rs;
    }

    return { q: rq, r: rr };
  }

  static getHexCorners(centerX: number, centerY: number, size: number, orientation: HexOrientation): Point[] {
    const corners: Point[] = [];
    const startAngle = orientation === HexOrientation.POINTY ? Math.PI / 6 : 0;

    for (let i = 0; i < 6; i++) {
      const angle = startAngle + i * Math.PI / 3;
      corners.push({
        x: centerX + size * Math.cos(angle),
        y: centerY + size * Math.sin(angle)
      });
    }

    return corners;
  }

  static getHexWidth(size: number, orientation: HexOrientation): number {
    return orientation === HexOrientation.POINTY ? this.SQRT3 * size : 2 * size;
  }

  static getHexHeight(size: number, orientation: HexOrientation): number {
    return orientation === HexOrientation.POINTY ? 2 * size : this.SQRT3 * size;
  }
}
